// src/components/AnimatedCircuitSequence.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import PathParticles from './PathParticles';
import { CircuitData, CircuitNode } from './CircuitBackground'; // 型定義をインポート

// selectRandomPath 関数は変更なし (前回の実装と同じ)
// ... (selectRandomPath 関数の定義) ...
const selectRandomPath = (
  circuitData: CircuitData,
  minLength: number = 4,
  maxLength: number = 8
): THREE.Vector3[] => {
  if (circuitData.nodes.length < minLength) return [];
  let attempts = 0;
  const maxAttempts = 20;
  while (attempts < maxAttempts) {
    const pathNodes: CircuitNode[] = [];
    let currentNode = circuitData.nodes[Math.floor(Math.random() * circuitData.nodes.length)];
    if (!currentNode) { attempts++; continue; }
    pathNodes.push(currentNode);
    const visitedNodeIds = new Set<string>([currentNode.id]);
    for (let i = 0; i < maxLength - 1; i++) {
      const possibleNextNodes = currentNode.connections
        .map(id => circuitData.nodes.find(n => n.id === id))
        .filter(n => n && !visitedNodeIds.has(n.id)) as CircuitNode[];
      if (possibleNextNodes.length === 0) break;
      currentNode = possibleNextNodes[Math.floor(Math.random() * possibleNextNodes.length)];
      pathNodes.push(currentNode);
      visitedNodeIds.add(currentNode.id);
    }
    if (pathNodes.length >= minLength) return pathNodes.map(n => n.position.clone());
    attempts++;
  }
  return [];
};


interface AnimatedCircuitSequenceProps {
  circuitData: CircuitData;
  pathChangeInterval?: number; // パーティクル走行時間 (ms)
  fadeDuration?: number;       // フェードイン/アウトの時間 (ms)
  particleCount?: number;
}

type AnimationPhase = "FADING_OUT" | "CHANGING_PATH" | "FADING_IN" | "RUNNING";

const AnimatedCircuitSequence: React.FC<AnimatedCircuitSequenceProps> = ({
  circuitData,
  pathChangeInterval = 4000, // パーティクルがアクティブに表示される時間
  fadeDuration = 1000,       // フェードイン・アウトにかける時間
  particleCount = 1,
}) => {
  const [currentPath, setCurrentPath] = useState<THREE.Vector3[]>([]);
  const [phase, setPhase] = useState<AnimationPhase>("CHANGING_PATH"); // 初期フェーズ
  
  // useFrameで滑らかにアニメーションさせるためのopacity管理
  const currentOpacityRef = useRef(0); // 現在の実際の透明度
  const targetOpacityRef = useRef(0);  // 目標とする透明度
  const [displayOpacity, setDisplayOpacity] = useState(0); // PathParticlesに渡す透明度

  // 新しいパスを選択し、フェーズを更新する関数
  const setupNewPath = useCallback(() => {
    if (circuitData.nodes.length > 0) {
      const newPath = selectRandomPath(circuitData);
      if (newPath.length > 1) {
        setCurrentPath(newPath);
        setPhase("FADING_IN");
        targetOpacityRef.current = 1; // フェードイン開始
      } else {
        // 有効なパスが見つからない場合、少し待って再度試行
        setPhase("CHANGING_PATH"); // CHANGING_PATHに戻して再試行を促す
      }
    }
  }, [circuitData]);

  // フェーズ管理とタイマー
  useEffect(() => {
    let timerId: NodeJS.Timeout;
    console.log("Current Phase:", phase);

    switch (phase) {
      case "FADING_OUT":
        targetOpacityRef.current = 0; // フェードアウト開始
        timerId = setTimeout(() => {
          setPhase("CHANGING_PATH");
        }, fadeDuration);
        break;
      case "CHANGING_PATH":
        // targetOpacityRef.current は既に0のはず
        // currentPathが更新されるのを待つか、すぐに新しいパスをセットアップ
        setupNewPath();
        break;
      case "FADING_IN":
        targetOpacityRef.current = 1; // フェードイン開始
        timerId = setTimeout(() => {
          setPhase("RUNNING");
        }, fadeDuration);
        break;
      case "RUNNING":
        // targetOpacityRef.current は既に1のはず
        timerId = setTimeout(() => {
          setPhase("FADING_OUT");
        }, pathChangeInterval);
        break;
    }
    return () => clearTimeout(timerId);
  }, [phase, setupNewPath, pathChangeInterval, fadeDuration]);

  // useFrameで透明度を滑らかに更新
  useFrame((state, delta) => {
    const currentOp = currentOpacityRef.current;
    const targetOp = targetOpacityRef.current;
    const speed = 1 / (fadeDuration / 1000); // 1秒あたりに変化する量 (フェード時間に基づく)

    if (currentOp < targetOp) { // フェードイン
      currentOpacityRef.current = Math.min(targetOp, currentOp + speed * delta);
    } else if (currentOp > targetOp) { // フェードアウト
      currentOpacityRef.current = Math.max(targetOp, currentOp - speed * delta);
    }
    setDisplayOpacity(currentOpacityRef.current);
  });

  return (
    <>
      {currentPath.length > 1 && (
        <PathParticles
          pathPoints={currentPath}
          count={particleCount}
          globalOpacity={displayOpacity} // アニメーション中の透明度を渡す
        />
      )}
    </>
  );
};

export default AnimatedCircuitSequence;